import React from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ContactInformationFormData,
  BusinessProfileFormData,
  PersonalProfileFormData,
  contactInformationSchema,
  businessProfileSchema,
  personalProfileSchema,
} from "./_validation";

type FormStep = "contact" | "business" | "personal" | "success";

interface FormContextType {
  step: FormStep;
  setStep: (step: FormStep) => void;
  contactMethods: UseFormReturn<ContactInformationFormData>;
  businessMethods: UseFormReturn<BusinessProfileFormData>;
  personalMethods: UseFormReturn<PersonalProfileFormData>;
  contactData: ContactInformationFormData | null;
  setContactData: (data: ContactInformationFormData) => void;
  handleBack: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  handleFormSubmit: (
    data: ContactInformationFormData | BusinessProfileFormData | PersonalProfileFormData,
    formType: "contact" | "business" | "personal"
  ) => Promise<void>;
}

const FormContext = React.createContext<FormContextType | undefined>(undefined);

interface CombinedFormData {
  contact: ContactInformationFormData;
  profile: BusinessProfileFormData | PersonalProfileFormData;
}

export const FormProvider = ({ children }: { children: React.ReactNode }) => {
  const [step, setStep] = React.useState<FormStep>("contact");
  const [contactData, setContactData] = React.useState<ContactInformationFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formData, setFormData] = React.useState<Partial<CombinedFormData>>({});

  const handleFormSubmit = async (
    data: ContactInformationFormData | BusinessProfileFormData | PersonalProfileFormData,
    formType: "contact" | "business" | "personal"
  ) => {
    setIsSubmitting(true);
    try {
      switch (formType) {
        case "contact":
          const contactFormData = data as ContactInformationFormData;
          setContactData(contactFormData);
          setFormData(prev => ({ ...prev, contact: contactFormData }));
          if (contactFormData.profileType !== "none") {
            setStep(contactFormData.profileType);
          }
          break;

        case "business":
        case "personal":
          if (!contactData) {
            throw new Error("Contact information is required");
          }

          const finalFormData = {
            contact: contactData,
            profile: data,
          };

          console.log("Final Form Submit:", finalFormData);
          await new Promise(resolve => setTimeout(resolve, 1000));
          setStep("success");
          break;
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = useForm<ContactInformationFormData>({
    resolver: zodResolver(contactInformationSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      profileType: "none",
    },
  });

  const businessMethods = useForm<BusinessProfileFormData>({
    resolver: zodResolver(businessProfileSchema),
    mode: "onBlur",
    defaultValues: {
      notificationPreferences: {
        email: false,
        sms: false,
      },
      privacyPolicy: false,
    },
  });

  const personalMethods = useForm<PersonalProfileFormData>({
    resolver: zodResolver(personalProfileSchema),
    mode: "onBlur",
    defaultValues: {
      notificationPreferences: {
        email: false,
        sms: false,
      },
      privacyPolicy: false,
    },
  });

  const handleBack = () => setStep("contact");

  return (
    <FormContext.Provider
      value={{
        step,
        setStep,
        contactMethods,
        businessMethods,
        personalMethods,
        contactData,
        setContactData,
        handleBack,
        isSubmitting,
        setIsSubmitting,
        handleFormSubmit,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = React.useContext(FormContext);
  if (context === undefined) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};
